import { useEffect, useRef, useState } from "react";
import { Camera, FileText, Image as ImmagineIcona, Paperclip } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  accettati,
  erroreDocumenti,
  etichettaCategoria,
  nomeAnonimo,
  pesoFile,
  problemaFile,
  senzaEstensione,
} from "@/lib/documenti";
import { cn } from "@/lib/utils";

/* Sul telefono si scatta la foto sul momento: è il caso del rilievo in cantiere.
   Su un computer con mouse il bottone non serve (aprirebbe lo stesso selettore). */
function conFotocamera() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse), (max-width: 639px)").matches;
}

function oggiBreve() {
  return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" })
    .format(new Date())
    .replaceAll("/", "-");
}

/**
 * Il caricamento. Due forme: un documento nuovo (si sceglie la categoria) o la
 * versione nuova di un documento che c'è (la categoria è già sua).
 *
 * `richiesta`: null (chiuso) · { categoria } · { documento }.
 */
export default function CaricaDocumento({ richiesta, regole, clienteId, onChiudi, onCaricato }) {
  const documento = richiesta?.documento || null;
  const [categoria, setCategoria] = useState(null);
  const [file, setFile] = useState(null);
  const [nome, setNome] = useState("");
  const [problema, setProblema] = useState(null); // del file: finché c'è, non si carica
  const [errore, setErrore] = useState(null); // del server: si riprova col bottone
  const [progresso, setProgresso] = useState(null);
  const inFile = useRef(null);
  const inFoto = useRef(null);
  const aperto = richiesta != null;

  useEffect(() => {
    if (!richiesta) return;
    setCategoria(richiesta.categoria || null);
    setFile(null);
    setNome(richiesta.documento ? senzaEstensione(richiesta.documento.nome) : "");
    setProblema(null);
    setErrore(null);
    setProgresso(null);
  }, [richiesta]);

  const scelto = (e, daFotocamera) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setProblema(problemaFile(f, regole));
    setErrore(null);
    if (documento) return;
    // Una foto scattata senza aver scelto la categoria è una foto.
    const cat = categoria || (daFotocamera ? "foto" : null);
    if (cat !== categoria) setCategoria(cat);
    // Un nome che dice qualcosa: quello del file se ne ha uno, sennò categoria e giorno.
    setNome(daFotocamera || nomeAnonimo(f.name) ? `${etichettaCategoria(regole, cat)} ${oggiBreve()}` : senzaEstensione(f.name));
  };

  const invia = async (e) => {
    e.preventDefault();
    if (!file || problema || (!documento && !categoria)) return;
    const dati = new FormData();
    dati.append("file", file);
    if (nome.trim()) dati.append("nome", nome.trim());
    if (!documento) dati.append("categoria", categoria);
    setErrore(null);
    setProgresso(0);
    try {
      const r = await api.post(documento ? `/documenti/${documento.id}/versioni` : `/documenti/clienti/${clienteId}`, dati, {
        onUploadProgress: (ev) => ev.total && setProgresso(Math.round((ev.loaded * 100) / ev.total)),
      });
      onCaricato(r.data);
    } catch (err) {
      setErrore(erroreDocumenti(err));
      setProgresso(null);
    }
  };

  const occupato = progresso !== null;
  const IconaFile = file && (file.type || "").startsWith("image/") ? ImmagineIcona : FileText;

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && !occupato && onChiudi()}>
      <DialogContent className="max-h-[92dvh] grid-cols-1 overflow-y-auto sm:max-w-lg" data-testid="documenti-carica-dialogo">
        <form onSubmit={invia} className="grid min-w-0 grid-cols-1 gap-card">
          <DialogHeader className="pr-card">
            <DialogTitle>{documento ? "Versione nuova" : "Carica un documento"}</DialogTitle>
            <DialogDescription>
              {documento
                ? `Sostituisce «${documento.nome}». La versione di adesso resta nell'archivio, segnata come superata.`
                : "Resta sulla scheda del cliente: lo vede e lo scarica chi segue il cliente, e il titolare."}
            </DialogDescription>
          </DialogHeader>

          {!documento && regole ? (
            <fieldset className="grid gap-inside" data-testid="documenti-categorie">
              <legend className="text-sm font-medium text-foreground">Che documento è</legend>
              <div className="mt-inside grid grid-cols-2 gap-inside">
                {regole.categorie.map((c) => {
                  const attiva = categoria === c.codice;
                  return (
                    <label
                      key={c.codice}
                      className={cn(
                        "flex min-h-11 cursor-pointer items-center gap-inside rounded-md border px-group py-inside text-sm transition-colors",
                        attiva
                          ? "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground"
                          : "border-border text-foreground hover:bg-accent",
                      )}
                    >
                      <input
                        type="radio"
                        name="categoria"
                        value={c.codice}
                        checked={attiva}
                        onChange={() => setCategoria(c.codice)}
                        className="h-4 w-4 shrink-0 accent-primary"
                        data-testid={`documenti-categoria-${c.codice}`}
                      />
                      <span className="min-w-0">{c.etichetta}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          <div className="grid grid-cols-1 gap-inside">
            <span className="text-sm font-medium text-foreground">Il file</span>
            <input
              ref={inFile}
              type="file"
              accept={accettati(regole)}
              className="sr-only"
              tabIndex={-1}
              aria-label="File da caricare"
              onChange={(e) => scelto(e, false)}
              data-testid="documenti-input-file"
            />
            <input
              ref={inFoto}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              tabIndex={-1}
              aria-label="Foto dalla fotocamera"
              onChange={(e) => scelto(e, true)}
              data-testid="documenti-input-foto"
            />
            {file ? (
              <div className="flex items-center gap-group rounded-md border border-border px-group py-inside" data-testid="documenti-file-scelto">
                <IconaFile className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">{file.name}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{pesoFile(file.size)}</p>
                </div>
                <Button type="button" variant="ghost" size="sm" className="h-11 sm:h-8" disabled={occupato} onClick={() => inFile.current?.click()}>
                  Cambia
                </Button>
              </div>
            ) : (
              <div className="grid gap-inside sm:grid-cols-2">
                <Button type="button" variant="outline" className="h-11" onClick={() => inFile.current?.click()} data-testid="documenti-scegli-file">
                  <Paperclip />
                  Scegli un file
                </Button>
                {conFotocamera() ? (
                  <Button type="button" variant="outline" className="h-11" onClick={() => inFoto.current?.click()} data-testid="documenti-scatta-foto">
                    <Camera />
                    Scatta una foto
                  </Button>
                ) : null}
              </div>
            )}
            {regole ? (
              <p className="text-xs text-muted-foreground">
                {regole.formati_testo}, fino a {regole.max_testo}.
              </p>
            ) : null}
          </div>

          {file ? (
            <div className="grid gap-inside">
              <Label htmlFor="documenti-nome">Nome nell'archivio</Label>
              <Input
                id="documenti-nome"
                value={nome}
                maxLength={180}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Es. Rilievo cucina, parete finestra"
                data-testid="documenti-nome"
              />
            </div>
          ) : null}

          {file && !documento && !categoria && !problema ? (
            <p className="text-sm text-muted-foreground" data-testid="documenti-manca-categoria">
              Scegli che documento è, poi carica.
            </p>
          ) : null}

          {problema || errore ? (
            <p role="alert" className="rounded-md bg-danger-subtle px-group py-inside text-sm text-danger-foreground" data-testid="documenti-problema">
              {problema || errore}
            </p>
          ) : null}

          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={occupato} onClick={onChiudi}>
              Annulla
            </Button>
            <Button
              type="submit"
              className="h-11 sm:h-9"
              disabled={occupato || !file || !!problema || (!documento && !categoria)}
              data-testid="documenti-invia"
            >
              {occupato ? `Carico… ${progresso}%` : documento ? "Carica la versione nuova" : "Carica"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
